import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "./FunnelLayout";
import "./CommonNewFunnel.css";

const LS_KEY = "glp1_intake_step6";

// ✅ QUESTION KEYS (API-ready)
const Q6_KEY =
  "Q6_Have_you_ever_tried_to_lose_weight_in_a_weight_management_program_jenny_craig_weight_watchers_etc";
const Q6A_KEY = "Q6a_Please_provide_brief_details";

// ✅ FULL ANSWERS ONLY
const Q6_ANSWERS = {
  YES: "Yes",
  NO: "No",
} as const;

type Q6Answer = (typeof Q6_ANSWERS)[keyof typeof Q6_ANSWERS] | "";

type StoredPayload = {
  [Q6_KEY]?: Q6Answer;
  [Q6A_KEY]?: string;
};

// UI-only state (minimal)
type FormState = {
  q6: Q6Answer;
  q6a: string;
};

const DEFAULT: FormState = { q6: "", q6a: "" };

type OptionCardProps = {
  type: "radio" | "checkbox";
  name?: string;
  label: string;
  checked: boolean;
  onChange: () => void;
};

const OptionCard: React.FC<OptionCardProps> = ({
  type,
  name,
  label,
  checked,
  onChange,
}) => {
  return (
    <button
      type="button"
      className={`funnel-option funnel-checkbox ${checked ? "is-selected" : ""}`}
      onClick={onChange}
    >
      <span className={`funnel-control ${type} ${checked ? "is-selected" : ""}`}>
        {type === "radio" ? <span className="dot" /> : <span className="tick" />}
      </span>

      <span className="funnel-option__label">{label}</span>

      <input
        className="sr-only"
        type={type}
        name={name}
        checked={checked}
        onChange={onChange}
      />
    </button>
  );
};

const Field: React.FC<{
  label: string;
  error?: boolean;
  children: React.ReactNode;
}> = ({ label, error, children }) => {
  return (
    <div className={`funnel-field ${error ? "has-error" : ""}`}>
      <div className="funnel-field__label">{label}</div>
      {children}
    </div>
  );
};

// ✅ read question->answer format from LS
const readFromLS = (): FormState => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT;

    const parsed = JSON.parse(raw) as StoredPayload;

    const q6 = parsed?.[Q6_KEY];
    const q6a = parsed?.[Q6A_KEY];

    const safeQ6: Q6Answer =
      q6 === Q6_ANSWERS.YES || q6 === Q6_ANSWERS.NO ? q6 : "";

    const safeQ6a = typeof q6a === "string" ? q6a : "";

    // if No, ignore any old Q6a
    if (safeQ6 === Q6_ANSWERS.NO) return { q6: safeQ6, q6a: "" };

    return { q6: safeQ6, q6a: safeQ6a };
  } catch {
    return DEFAULT;
  }
};

// ✅ build question->answer payload
const buildPayload = (form: FormState): StoredPayload => {
  const payload: StoredPayload = {
    [Q6_KEY]: form.q6,
  };

  if (form.q6 === Q6_ANSWERS.YES) {
    payload[Q6A_KEY] = form.q6a.trim();
  }

  return payload;
};

const NewFunnelStep6: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(DEFAULT);
  const [touched, setTouched] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // ✅ HYDRATE once
  useEffect(() => {
    setForm(readFromLS());
    setHydrated(true);
  }, []);

  // ✅ SAVE only after hydration
  useEffect(() => {
    if (!hydrated) return;

    // if nothing selected, you can either store empty or remove.
    // I prefer: store only after selection to avoid garbage LS.
    if (!form.q6) return;

    localStorage.setItem(LS_KEY, JSON.stringify(buildPayload(form)));
  }, [form, hydrated]);

  const onChangeQ6 = (val: Q6Answer) => {
    setForm((p) => ({
      ...p,
      q6: val,
      q6a: val === Q6_ANSWERS.NO ? "" : p.q6a, // ✅ clear details if No
    }));
  };

  const canGoNext =
    form.q6 === Q6_ANSWERS.NO ||
    (form.q6 === Q6_ANSWERS.YES && form.q6a.trim().length > 0);

  const onNext = () => {
    setTouched(true);
    if (!canGoNext) return;

    // ✅ ensure latest saved right before navigation (extra safe)
    localStorage.setItem(LS_KEY, JSON.stringify(buildPayload(form)));

    navigate("/glp1/intake-form/step-6");
  };

  return (
    <FunnelLayout>
      <div className="funnel-section">
        <div className="funnel-q-title funnel-q-title-heading">
          How about weight loss programs?
        </div>

        <div className="funnel-q-title" style={{ marginBottom: "20px" }}>
          Have you ever tried to lose weight in a weight management program
          (Jenny Craig, Weight Watchers, etc)?
        </div>

        <OptionCard
          type="radio"
          name="q6"
          label={Q6_ANSWERS.YES}
          checked={form.q6 === Q6_ANSWERS.YES}
          onChange={() => onChangeQ6(Q6_ANSWERS.YES)}
        />

        <OptionCard
          type="radio"
          name="q6"
          label={Q6_ANSWERS.NO}
          checked={form.q6 === Q6_ANSWERS.NO}
          onChange={() => onChangeQ6(Q6_ANSWERS.NO)}
        />

        {touched && !form.q6 && (
          <div className="funnel-error">Please select one option.</div>
        )}

        {form.q6 === Q6_ANSWERS.YES && (
          <div style={{ marginTop: 16 }}>
            <div className="funnel-q-title">Please provide brief details.</div>

            <Field label="" error={touched && !form.q6a.trim()}>
              <textarea
                className="funnel-input"
                value={form.q6a}
                onChange={(e) => setForm((p) => ({ ...p, q6a: e.target.value }))}
                rows={4}
              />
            </Field>

            {touched && !form.q6a.trim() && (
              <div className="funnel-error">Please provide brief details.</div>
            )}
          </div>
        )}
      </div>

      <button className="next-btn" onClick={onNext} disabled={!canGoNext}>
        Next →
      </button>
    </FunnelLayout>
  );
};

export default NewFunnelStep6;